import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';

const adminLinks = [
  { label: 'Dashboard', path: '/admin' },
  { label: 'Alumnos', path: '/admin/students' },
  { label: 'Cinturones', path: '/admin/belts' },
  { label: 'Eventos', path: '/admin/events' },
  { label: 'Contenido', path: '/admin/content' },
  { label: 'Tarifas', path: '/admin/tariffs' },
  { label: 'Admins', path: '/admin/admins' },
];

const AdminLayout = () => {
  const { logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const linkClass = ({ isActive }) =>
    `inline-flex min-h-9 items-center rounded-md px-2.5 text-sm font-medium transition-colors ${
      isActive ? 'bg-white/20 text-white' : 'text-white/85 hover:bg-white/10 hover:text-white'
    }`;

  return (
    <div className={`admin-shell flex min-h-screen flex-col ${mode === 'dark' ? 'dark' : ''}`}>
      <header className="sticky top-0 z-40 bg-primary text-white shadow-sm">
        <div className="mx-auto flex h-14 max-w-content items-center gap-3 px-4 md:h-16 md:px-6">
          <Link to="/admin" className="shrink-0 text-base font-bold tracking-tight md:text-lg">
            Kitsune Admin
          </Link>

          <nav className="hidden flex-1 items-center gap-0.5 lg:flex" aria-label="Admin">
            {adminLinks.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === '/admin'}
                className={linkClass}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              className="inline-flex min-h-10 min-w-10 items-center justify-center rounded-md hover:bg-white/10"
              onClick={toggleTheme}
              aria-label={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
            >
              {mode === 'dark' ? '☀' : '☾'}
            </button>
            <Link
              to="/"
              className="hidden min-h-10 items-center rounded-md px-3 text-sm font-medium hover:bg-white/10 sm:inline-flex"
            >
              Sitio
            </Link>
            <button
              type="button"
              className="hidden min-h-10 items-center rounded-md px-3 text-sm font-medium hover:bg-white/10 sm:inline-flex"
              onClick={handleLogout}
            >
              Salir
            </button>
            <button
              type="button"
              className="inline-flex min-h-10 min-w-10 items-center justify-center rounded-md border border-white/30 lg:hidden"
              aria-expanded={menuOpen}
              aria-controls="admin-mobile-nav"
              aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                {menuOpen ? (
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                ) : (
                  <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div
            id="admin-mobile-nav"
            className="fixed inset-0 top-14 z-50 flex flex-col bg-surface px-4 py-6 text-ink lg:hidden"
          >
            <nav className="flex flex-col gap-1" aria-label="Admin móvil">
              {adminLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  end={link.path === '/admin'}
                  className={({ isActive }) =>
                    `flex min-h-12 items-center border-b border-line text-lg font-semibold ${
                      isActive ? 'text-primary' : 'text-ink'
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
            <div className="mt-8 flex flex-col gap-3">
              <Link to="/" className="btn-admin-secondary w-full">
                Ir al sitio
              </Link>
              <button type="button" className="btn-admin w-full" onClick={handleLogout}>
                Cerrar sesión
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
